import { useScrollReveal } from "@/hooks/useScrollReveal";
import { lean } from "@/content";
import { Rich } from "@/lib/rich";

const steps = lean.methodology.steps;

export default function LeanMethodology() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section id="methodology" className="py-24 relative overflow-hidden">
      <div className="absolute top-[15%] left-[-5%] w-[350px] h-[350px] rounded-full bg-orange-100/30 blur-[130px] dark:bg-orange-500/10 pointer-events-none" />
      <div className="absolute bottom-[10%] right-[-5%] w-[300px] h-[300px] rounded-full bg-stone-200/40 blur-[100px] dark:bg-white/[0.04] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${
            revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-stone-100 border border-stone-200 text-stone-600 dark:bg-white/[0.06] dark:border-white/[0.10] dark:text-white/60 text-xs font-semibold tracking-widest uppercase mb-6">
            {lean.methodology.badge}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-stone-900 leading-tight mb-4 dark:text-white">
            <Rich text={lean.methodology.title} accentClassName="text-orange-600 dark:text-orange-500" />
          </h2>
          <p className="text-stone-600 text-lg leading-relaxed max-w-2xl dark:text-white/60 mx-auto">
            {lean.methodology.subtitle}
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Desktop line */}
          <div className="hidden lg:block absolute top-[72px] left-[10%] right-[10%] h-px bg-gradient-to-r from-stone-200 via-orange-300 to-stone-200 dark:from-white/[0.14] dark:via-orange-500/40 dark:to-white/[0.14]" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <StepCard key={step.num} step={step} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function StepCard({
  step,
  index,
}: {
  step: { num: string; title: string; desc: string; icon: string };
  index: number;
}) {
  const { ref, revealed } = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`relative flex flex-col items-center text-center transition-all duration-700 ${
        revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-orange-50 border border-orange-200 mb-5 relative z-10 dark:bg-orange-500/[0.12] dark:border-orange-400/25">
        <i className={`${step.icon} text-orange-600 text-2xl dark:text-orange-400`}></i>
      </div>
      <span className="text-orange-600 font-extrabold text-2xl dark:text-orange-400 mb-2">{step.num}</span>
      <h4 className="text-stone-900 font-bold text-base dark:text-white mb-2">{step.title}</h4>
      <p className="text-stone-600 text-sm leading-relaxed dark:text-white/60">{step.desc}</p>
    </div>
  );
}